import {
  createEngagementSession,
  EngagementSessionError,
  type CreateSessionOptions,
  type EngagementSession,
} from '../engagement/session';
import {
  openConversation,
  type Conversation,
} from '../engagement/conversation';
import type { BotTurn } from '../engagement/bot-turn';
import type { TranscriptEntry } from '../types';

/**
 * One exchange in the transcript.
 *
 * `role` rather than a bot/user pair of arrays, so the ORDER is the data —
 * ordering is exactly what went wrong in the tool this replaces, where a
 * straggler frame got attributed to the following turn.
 *
 * The declaration lives in `types.ts`, which has no runtime dependencies, so
 * the client can hold the type without importing this module.
 */
export type { TranscriptEntry } from '../types';

export interface ConversationRun {
  chatId: string;
  engagementApiUrl: string;
  transcript: TranscriptEntry[];
  /**
   * True when the bot said nothing unprompted. Worth reporting: it is normal
   * for some solutions and a symptom for others, and the caller can see
   * which by looking at the transcript.
   */
  greetingMissing: boolean;
}

export interface RunConversationOptions {
  appId: string;
  engagementApiUrl: string;
  /** The user messages to send, in order. */
  messages: string[];
  createSession?: (opts: CreateSessionOptions) => Promise<EngagementSession>;
  openConv?: typeof openConversation;
  signal?: AbortSignal;
}

const toEntry = (turn: BotTurn): TranscriptEntry => ({
  role: 'bot',
  text: turn.text,
  ...(turn.options.length ? { options: turn.options } : {}),
});

/**
 * Open a conversation, send the given messages, and return the transcript.
 *
 * The greeting is drained BEFORE the first user message, not merged into its
 * reply. Both matter: the greeting is content a test may want to assert on,
 * and leaving it in the inbox would put it inside the first reply's window,
 * attributing the bot's opening line to a question it does not answer.
 */
export const runConversation = async ({
  appId,
  engagementApiUrl,
  messages,
  createSession = createEngagementSession,
  openConv = openConversation,
  signal,
}: RunConversationOptions): Promise<ConversationRun> => {
  const session = await createSession({ appId, engagementApiUrl, signal });

  let conversation: Conversation | undefined;
  try {
    // The signal goes to the CONVERSATION too, not only to session creation.
    // The socket, the handshake, the greeting and every reply happen after the
    // session exists, so a run budget that stopped at `createSession` bounded
    // the fastest step and left the slow ones open-ended.
    conversation = await openConv({
      appId,
      session,
      engagementApiUrl,
      signal,
    });

    const transcript: TranscriptEntry[] = [];

    // A greeting with neither text nor options is not a greeting. The bot can
    // emit a content frame carrying no `msg` at all, which produced a
    // transcript entry rendering as an "Opening message" heading with nothing
    // under it — worse than reporting none, because it looks like content that
    // failed to load.
    const greetingTurn = await conversation.greeting();
    const greeting =
      greetingTurn && (greetingTurn.text || greetingTurn.options.length > 0)
        ? greetingTurn
        : null;
    if (greeting) transcript.push(toEntry(greeting));

    for (const message of messages) {
      transcript.push({ role: 'user', text: message });
      // Strictly sequential: the bot's reply to turn N is the context for
      // turn N+1, and overlapping sends would interleave the reply windows.
      // eslint-disable-next-line no-await-in-loop
      transcript.push(toEntry(await conversation.send(message)));
    }

    return {
      chatId: session.chatId,
      engagementApiUrl,
      transcript,
      greetingMissing: greeting === null,
    };
  } finally {
    // Always: an open socket outlives the request otherwise, and a serverless
    // invocation that leaves one behind is billed for it.
    conversation?.close();
  }
};

export { EngagementSessionError };
